import React, { Fragment } from 'react';
import { useGetTermsStatus } from '../../api/terms';
import TermsAcceptanceModal from './terms-acceptance-modal';

/**
 * Gating wrapper: renders children, but overlays the
 * TermsAcceptanceModal when the active customer has not yet accepted
 * the current Terms & Conditions version. The modal is global and
 * blocks interaction with the underlying app — the only path forward
 * is to accept (or sign out).
 *
 * Behavior:
 *  - On a brand-new user with no Customer yet, the status endpoint
 *    returns customerID=null and requires_acceptance=true. We don't
 *    show the modal in that case — the customer setup flow needs to
 *    run first to create a Customer. Once setup completes and the
 *    user reloads / re-fetches, this gate kicks in.
 *  - For existing customers who haven't accepted any version yet, the
 *    modal blocks on next login.
 *  - For existing customers when CURRENT_TC_VERSION bumps, the modal
 *    blocks until they accept the new version.
 *  - While the status is loading (initial fetch), children are
 *    rendered to avoid flashing the modal on every page load. This is
 *    safe because the backend authorization for any sensitive action
 *    still gates per-request.
 */
export default function TermsAcceptanceRequired({ children }) {
    const { data, isLoading, mutate } = useGetTermsStatus();

    const shouldBlock =
        !isLoading &&
        data &&
        data.customerID && // only block when there's an actual customer to accept for
        data.requires_acceptance === true &&
        data.can_accept !== false; // never gate users who aren't the customer owner

    return (
        <Fragment>
            {children}
            {shouldBlock ? (
                <TermsAcceptanceModal
                    currentVersion={data.current_version}
                    publicTcUrl={data.public_tc_url}
                    acceptedVersion={data.accepted_version}
                    onAccepted={() => mutate()}
                />
            ) : null}
        </Fragment>
    );
}
